/* ═══════════════════════════════════════════════════════
   SwipeHome — Profile Module
   Buyer profile, agent profile, onboarding flow,
   swipe milestones, onboarding progress
   ═══════════════════════════════════════════════════════ */

import { api } from './api.js';
import { clearInvestorAssumptions } from './investorDetail.js';
import { renderRecentlyViewedRow } from './listings.js';
import { navigate, render, renderTabBar } from './router.js';
import { clearFeedState, state } from './state.js';
import { compactMagnitude } from '../../shared/format.js';
import { PRIVACY_URL, SUPPORT_MAILTO, TERMS_URL } from '../../shared/legal.js';
import type { PropertyType } from '../../shared/types.js';
import {
  closeModal,
  escapeHtml,
  getInitials,
  getUserName,
  openModal,
  showToast,
} from './ui.js';

// Extended state stash for scratch fields not on AppState.
// ═══════════════════════════════════════════════════════
// Investor Profile — replaces the buyer-style Design DNA block on the
// consumer profile screen with the investment criteria captured during
// investor onboarding (strategy, markets, cap-rate floor, DSCR floor,
// portfolio size). Renders a "Set up your investor profile" CTA when
// investment_criteria is empty — handles legacy buyer accounts that
// never went through investor onboarding.
// ═══════════════════════════════════════════════════════

interface InvestmentCriteriaShape {
  strategy?: string;
  target_markets?: string[];
  min_price?: number;
  max_price?: number;
  min_cap_rate?: number;
  min_cash_on_cash?: number;
  min_dscr?: number;
  property_types?: string[];
  financing_type?: string;
  portfolio_size?: number;
}

const STRATEGY_LABELS: Record<string, string> = {
  buy_and_hold: 'Buy & Hold',
  flip: 'Fix & Flip',
  brrrr: 'BRRRR',
  short_term_rental: 'Short-Term Rental',
  wholesale: 'Wholesale',
};

const FINANCING_LABELS: Record<string, string> = {
  conventional: 'Conventional',
  dscr: 'DSCR Loan',
  hard_money: 'Hard Money',
  cash: 'Cash',
};

const PROPERTY_TYPE_LABELS: Record<string, string> = {
  single_family: 'Single-Family',
  multi_family: 'Multi-Family',
  condo: 'Condo',
  townhouse: 'Townhouse',
  duplex: 'Duplex',
};

// Round-then-pick-unit via the shared formatter: $999,600 reads "$1M",
// never "$1000K".
function _formatMoney(n: number): string {
  return `$${compactMagnitude(n, { millionDecimals: 2, trimMillionZero: true })}`;
}

export function renderProfile(): HTMLElement {
  const div = document.createElement('div');
  div.className = 'fade-in';
  div.innerHTML = `
    ${renderIdentityHeader()}
    <div id="investor-profile-content"></div>
    <div id="profile-recently-viewed-slot" class="profile-rv-slot">${renderRecentlyViewedRow()}</div>
    ${renderSubscriptionSection()}
    ${renderLegalSection()}
    <div class="profile-account-section">
      <div class="profile-section-title">Account</div>
      <button class="btn btn-secondary btn-block profile-signout-btn" type="button" onclick="handleLogout()">
        <i data-feather="log-out" width="14" style="margin-right:6px"></i>Sign out
      </button>
    </div>
  `;
  // Populate after insertion
  setTimeout(() => loadInvestorProfile(), 0);
  return div;
}

/**
 * Who is signed in — name, email, role label — from the cached
 * /api/users/me payload in state.user (kept in sync on login and by
 * updateProfile). Audit 2026-09-24: the tab showed neither identity nor
 * a way out; the sign-out control lives in the Account section below.
 */
function renderIdentityHeader(): string {
  const user = (state.user || {}) as Parameters<typeof getUserName>[0] & { email?: string | null };
  const name = getUserName(user) || 'Investor';
  const email = user?.email || '';
  return `
    <div class="profile-identity">
      <div class="profile-identity-avatar" aria-hidden="true">${escapeHtml(getInitials(name))}</div>
      <div class="profile-identity-text">
        <div class="profile-identity-name">${escapeHtml(name)}</div>
        ${email ? `<div class="profile-identity-email">${escapeHtml(email)}</div>` : ''}
        <div class="profile-identity-role">Investor</div>
      </div>
      <button class="profile-identity-edit" type="button" onclick="editProfile()" aria-label="Edit profile">
        <i data-feather="edit-2" width="14"></i>
      </button>
    </div>
  `;
}

/**
 * Subscription — Terms §10.4 says Pro can be cancelled from account
 * settings in the app, so this is where that control lives. Billing
 * isn't wired up yet (no Stripe / store purchases; `subscription_tier`
 * is a read-only column defaulting to 'free'), so the group states that
 * plainly instead of inventing a cancel flow.
 */
function renderSubscriptionSection(): string {
  const tier = (state.user as { subscription_tier?: string | null } | null)?.subscription_tier || 'free';
  const label = tier === 'free' ? 'Free' : tier.charAt(0).toUpperCase() + tier.slice(1);
  return `
    <div class="profile-account-section">
      <div class="profile-section-title">Subscription</div>
      <div class="profile-link-list">
        <div class="profile-link-row profile-link-row-static" role="group" aria-label="Current plan: ${escapeHtml(label)}">
          <i data-feather="star" width="14"></i>
          <span class="profile-link-label">Current plan</span>
          <span class="profile-link-value">${escapeHtml(label)}</span>
        </div>
      </div>
      <p class="profile-section-note">
        Pro isn't available yet. When it launches, you'll be able to manage or cancel your subscription from here.
      </p>
    </div>
  `;
}

/**
 * Legal — the Terms and Privacy Policy live on the marketing site; the app
 * only links to them (Terms §12 / Privacy §6 route requests to support@).
 */
function renderLegalSection(): string {
  const row = (href: string, icon: string, label: string, external: boolean) => `
    <a class="profile-link-row" href="${href}"${external ? ' target="_blank" rel="noopener"' : ''}>
      <i data-feather="${icon}" width="14"></i>
      <span class="profile-link-label">${label}</span>
      <i data-feather="${external ? 'external-link' : 'chevron-right'}" width="14" class="profile-link-chev"></i>
    </a>
  `;
  return `
    <div class="profile-account-section">
      <div class="profile-section-title">Legal</div>
      <div class="profile-link-list">
        ${row(TERMS_URL, 'file-text', 'Terms of Service', true)}
        ${row(PRIVACY_URL, 'shield', 'Privacy Policy', true)}
        ${row(SUPPORT_MAILTO, 'mail', 'Contact support', false)}
      </div>
    </div>
  `;
}

export function loadInvestorProfile(): void {
  const container = document.getElementById('investor-profile-content');
  if (!container) return;

  const criteria = (state.user?.investment_criteria as InvestmentCriteriaShape | undefined) || {};
  const hasAny =
    !!criteria.strategy
    || (criteria.target_markets && criteria.target_markets.length > 0)
    || criteria.min_cap_rate != null
    || criteria.min_cash_on_cash != null
    || criteria.min_dscr != null
    || criteria.portfolio_size != null;

  if (!hasAny) {
    container.innerHTML = `
      <div style="padding:14px;background:var(--primary-bg);border:1px solid var(--primary-border);border-radius:10px">
        <div style="display:flex;align-items:flex-start;gap:10px;margin-bottom:10px">
          <i data-feather="trending-up" width="18" style="color:var(--primary);margin-top:2px"></i>
          <div>
            <div style="font-weight:700;font-size:14px;color:var(--text-primary);margin-bottom:2px">Tell the engine what you're looking for</div>
            <div style="font-size:12px;color:var(--text-secondary);line-height:1.5">A 2-minute setup: strategy, target markets, cap-rate floor. We use it to score every deal in your feed.</div>
          </div>
        </div>
        <button class="btn btn-primary btn-block" onclick="navigate('investorOnboarding')" style="margin-top:4px">
          <i data-feather="arrow-right" width="14" style="margin-right:6px"></i>Set up investor profile
        </button>
      </div>
    `;
    if (window.feather) window.feather.replace();
    return;
  }

  const strategyChip = criteria.strategy
    ? `<span style="background:var(--primary);color:#fff;padding:6px 14px;border-radius:18px;font-weight:700;font-size:13px">${escapeHtml(STRATEGY_LABELS[criteria.strategy] || criteria.strategy)}</span>`
    : '';

  const marketsRow = criteria.target_markets && criteria.target_markets.length
    ? `
      <div style="margin-bottom:12px">
        <div style="font-size:11px;font-weight:700;color:var(--text-secondary);text-transform:uppercase;letter-spacing:0.5px;margin-bottom:6px">Target Markets</div>
        <div style="display:flex;flex-wrap:wrap;gap:6px">
          ${criteria.target_markets.map((m) => `<span style="background:var(--surface);color:var(--text-primary);border:1px solid var(--border);padding:3px 10px;border-radius:12px;font-size:12px;font-weight:600">${escapeHtml(m)}</span>`).join('')}
        </div>
      </div>
    ` : '';

  const propTypesRow = criteria.property_types && criteria.property_types.length
    ? `
      <div style="margin-bottom:12px">
        <div style="font-size:11px;font-weight:700;color:var(--text-secondary);text-transform:uppercase;letter-spacing:0.5px;margin-bottom:6px">Property Types</div>
        <div style="display:flex;flex-wrap:wrap;gap:6px">
          ${criteria.property_types.map((p) => `<span style="background:var(--surface);color:var(--text-primary);border:1px solid var(--border);padding:3px 10px;border-radius:12px;font-size:12px;font-weight:600">${escapeHtml(PROPERTY_TYPE_LABELS[p] || p)}</span>`).join('')}
        </div>
      </div>
    ` : '';

  const floorTiles: string[] = [];
  if (criteria.min_cap_rate != null) {
    floorTiles.push(`
      <div style="flex:1;background:var(--surface);border-radius:8px;padding:10px;text-align:center;min-width:90px">
        <div style="font-size:11px;color:var(--text-secondary);margin-bottom:2px">Min Cap Rate</div>
        <div style="font-size:14px;font-weight:700;color:var(--primary)">${criteria.min_cap_rate.toFixed(1)}%</div>
      </div>
    `);
  }
  if (criteria.min_cash_on_cash != null) {
    floorTiles.push(`
      <div style="flex:1;background:var(--surface);border-radius:8px;padding:10px;text-align:center;min-width:90px">
        <div style="font-size:11px;color:var(--text-secondary);margin-bottom:2px">Min CoC</div>
        <div style="font-size:14px;font-weight:700;color:var(--primary)">${criteria.min_cash_on_cash.toFixed(1)}%</div>
      </div>
    `);
  }
  if (criteria.min_dscr != null) {
    floorTiles.push(`
      <div style="flex:1;background:var(--surface);border-radius:8px;padding:10px;text-align:center;min-width:90px">
        <div style="font-size:11px;color:var(--text-secondary);margin-bottom:2px">Min DSCR</div>
        <div style="font-size:14px;font-weight:700;color:var(--primary)">${criteria.min_dscr.toFixed(2)}</div>
      </div>
    `);
  }

  const floorsRow = floorTiles.length
    ? `<div style="display:flex;gap:8px;margin-bottom:12px;flex-wrap:wrap">${floorTiles.join('')}</div>`
    : '';

  const priceRange = criteria.min_price != null && criteria.max_price != null
    ? `${_formatMoney(criteria.min_price)} – ${_formatMoney(criteria.max_price)}`
    : null;

  const sideTiles: string[] = [];
  if (priceRange) {
    sideTiles.push(`
      <div style="flex:1;background:var(--surface);border-radius:8px;padding:10px;text-align:center;min-width:120px">
        <div style="font-size:11px;color:var(--text-secondary);margin-bottom:2px">Price Range</div>
        <div style="font-size:13px;font-weight:700">${priceRange}</div>
      </div>
    `);
  }
  if (criteria.portfolio_size != null) {
    sideTiles.push(`
      <div style="flex:1;background:var(--surface);border-radius:8px;padding:10px;text-align:center;min-width:120px">
        <div style="font-size:11px;color:var(--text-secondary);margin-bottom:2px">Portfolio Goal</div>
        <div style="font-size:13px;font-weight:700">${criteria.portfolio_size} ${criteria.portfolio_size === 1 ? 'door' : 'doors'}</div>
      </div>
    `);
  }
  if (criteria.financing_type) {
    sideTiles.push(`
      <div style="flex:1;background:var(--surface);border-radius:8px;padding:10px;text-align:center;min-width:120px">
        <div style="font-size:11px;color:var(--text-secondary);margin-bottom:2px">Financing</div>
        <div style="font-size:13px;font-weight:700">${escapeHtml(FINANCING_LABELS[criteria.financing_type] || criteria.financing_type)}</div>
      </div>
    `);
  }
  const sideRow = sideTiles.length
    ? `<div style="display:flex;gap:8px;margin-bottom:12px;flex-wrap:wrap">${sideTiles.join('')}</div>`
    : '';

  container.innerHTML = `
    ${strategyChip ? `<div style="margin-bottom:14px">${strategyChip}</div>` : ''}
    ${floorsRow}
    ${sideRow}
    ${marketsRow}
    ${propTypesRow}
    <button class="btn btn-secondary btn-block" onclick="navigate('investorOnboarding')" style="margin-top:6px;font-size:13px">
      <i data-feather="edit-2" width="13" style="margin-right:6px"></i>Update investment criteria
    </button>
    ${isDemoAccount() ? `
    <button class="btn btn-block" onclick="resetDemoProfile()" style="margin-top:8px;font-size:13px;background:transparent;border:1px dashed var(--border);color:var(--text-secondary)">
      <i data-feather="rotate-ccw" width="13" style="margin-right:6px"></i>Reset demo profile
    </button>` : ''}
  `;
  if (window.feather) window.feather.replace();
}

// ── Demo account ──────────────────────────────────────────────────────
// The demo runs on a single investor account (marcus.johnson@gmail.com).
// The reset affordance below is only rendered for that account, and the
// backend endpoint 403s for anyone else.
const DEMO_ACCOUNT_EMAIL = 'marcus.johnson@gmail.com';

function isDemoAccount(): boolean {
  return (state.user?.email || '').toLowerCase() === DEMO_ACCOUNT_EMAIL;
}

/**
 * "Reset demo profile" — confirm in the app's own bottom sheet, then run
 * the reset. The native window.confirm() used briefly after #215 is
 * auto-dismissed in installed PWAs and automated browsers, which made
 * the button a silent no-op; the modal is plain DOM and always shows.
 */
export function resetDemoProfile(): void {
  openModal(
    'Reset demo profile?',
    `<p style="font-size:14px;color:var(--text-secondary);line-height:1.6;margin:0 0 6px">
       Restores the canonical investment criteria and returns every Watchlist
       card to <strong style="color:var(--text)">Analyzing</strong>.
     </p>
     <p style="font-size:12px;color:var(--text-muted);margin:0">Owned properties and logged transactions are kept.</p>`,
    [
      { label: 'Cancel', style: 'secondary', onclick: closeModal },
      { label: 'Reset demo', style: 'danger', onclick: () => { closeModal(); void runDemoReset(); } },
    ],
  );
}

export async function runDemoReset(): Promise<void> {
  try {
    const data = await api<{ investment_criteria: Record<string, unknown>; pipeline_reset?: number }>(
      'POST',
      '/users/me/demo-reset',
      {},
    );
    // Sync local user cache so subsequent UI reads the canonical profile.
    if (state.user) {
      (state.user as { investment_criteria?: Record<string, unknown> }).investment_criteria =
        data.investment_criteria || {};
      localStorage.setItem('swipehome_user', JSON.stringify(state.user));
    }
    // Every cached deal number is stale now: drop per-listing slider state
    // and force the feed to refetch so cards re-render under the canonical
    // DSCR profile (same invalidation as the onboarding save flow).
    clearInvestorAssumptions();
    for (const mode of Object.values(state.feeds)) {
      mode.items = [];
      mode.loaded = false;
    }
    const n = Number(data.pipeline_reset ?? 0);
    showToast(n > 0 ? `Demo profile reset · ${n} Watchlist card${n === 1 ? '' : 's'} back to Analyzing` : 'Demo profile reset', 'success');
    navigateToHome();
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Could not reset demo profile';
    showToast(msg, 'error');
  }
}

// ═══════════════════════════════════════════════════════
// Agent Profile (unified for both agent types)


export function editProfile(): void {
  const user = (state.user || {}) as Parameters<typeof getUserName>[0];
  const modal = document.getElementById('modal-container');
  if (!modal) return;
  modal.classList.remove('hidden');
  modal.innerHTML = `
    <div class="modal-overlay" onclick="closeModal()"></div>
    <div class="modal-sheet">
      <div class="modal-handle"></div>
      <div class="modal-header">
        <h2>Edit Profile</h2>
        <button class="modal-close" onclick="closeModal()"><i data-feather="x" width="20"></i></button>
      </div>
      <div class="modal-body">
        <div class="input-group">
          <label>Full Name</label>
          <input type="text" class="input" id="edit-name" value="${escapeHtml(getUserName(user))}">
        </div>
        <div class="input-group">
          <label>Phone</label>
          <input type="tel" class="input" id="edit-phone" value="${escapeHtml(state.user?.phone || '')}">
        </div>
      </div>
      <div class="modal-footer">
        <button class="btn btn-primary btn-block" onclick="saveProfile()">Save Changes</button>
      </div>
    </div>
  `;
  if (window.feather) window.feather.replace();
}

export async function saveProfile(): Promise<void> {
  const fullName = (document.getElementById('edit-name') as HTMLInputElement | null)?.value?.trim();
  const phone = (document.getElementById('edit-phone') as HTMLInputElement | null)?.value?.trim();

  if (!fullName) {
    showToast('Name is required', 'error');
    return;
  }

  try {
    await api('PATCH', '/users/profile', { full_name: fullName, phone });
    if (state.user) {
      state.user = { ...state.user, full_name: fullName, phone: phone || null };
      localStorage.setItem('swipehome_user', JSON.stringify(state.user));
    }
    showToast('Profile updated!', 'success');
    closeModal();
    render();
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to save profile';
    showToast(message, 'error');
  }
}




export function handleLogout(): void {
  state.token = null;
  state.user = null;
  state.role = null;
  state.screenStack = [];
  state.feed = [];
  state.likes = [];
  state.notifications = [];
  state.clients = [];
  state.myListings = [];
  localStorage.removeItem('swipehome_token');
  localStorage.removeItem('swipehome_user');
  clearFeedState();
  clearInvestorAssumptions();
  document.body.style.overflow = '';
  navigate('login');
}

export async function submitPrivacyRequest(requestType: string): Promise<void> {
  try {
    const data = await api<{ message?: string }>('GET', `/privacy/data-request?request_type=${requestType}`);
    showToast(data.message || 'Privacy request submitted', 'success');
  } catch {
    showToast('Failed to submit privacy request', 'error');
  }
}

// ─── Home Navigation Helper ───────────────────────────

export function navigateToHome(): void {
  state.screenStack = [];
  if (state.role === 'buyer') {
    const userId = state.user?.id || '';
    const onboarded =
      localStorage.getItem(`swipehome_onboarding_complete_${userId}`) ||
      localStorage.getItem('swipehome_onboarding_complete');
    const userPrefs = state.user?.preferences as { max_price?: number; location_text?: string } | null | undefined;
    const hasPreferences = !!(userPrefs && (userPrefs.max_price || userPrefs.location_text));
    // Investor-first convergence: a populated investment_criteria signals
    // that the user has finished investor onboarding. Treat it as a
    // completion marker so returning investors aren't re-onboarded.
    const criteria = state.user?.investment_criteria as Record<string, unknown> | undefined;
    const hasInvestorCriteria = !!(criteria && Object.keys(criteria).length > 0);
    if (!onboarded && !hasPreferences && !hasInvestorCriteria) {
      // Route to investor onboarding (single consumer onboarding flow).
      navigate('investorOnboarding');
      return;
    }
    navigate('feed');
  } else {
    navigate('feed');
  }

  loadNotificationCount();

  if (state.role === 'buyer' && !state.preferences) {
    loadPreferences();
  }
}

export async function loadNotificationCount(): Promise<void> {
  try {
    const data = await api<{ notifications?: { read_at?: string | null }[] }>('GET', '/notifications?limit=50');
    const notifs = data.notifications || [];
    state.unreadCount = notifs.filter((n) => !n.read_at).length;
    const tabBar = document.getElementById('tab-bar');
    if (tabBar) renderTabBar(tabBar);
    if (window.feather) window.feather.replace();
  } catch {
    /* ignore */
  }
}

// ─── Load Preferences on Login ────────────────────────
export async function loadPreferences(): Promise<void> {
  try {
    const data = await api<{ preferences?: unknown } | unknown>('GET', '/users/preferences');
    const d = data as { preferences?: unknown };
    state.preferences = d.preferences || data || {};
  } catch {
    /* ignore */
  }
}

// ═══════════════════════════════════════════════════════
// Buyer Onboarding Flow (5 steps)
// ═══════════════════════════════════════════════════════

export function renderOnboarding(): HTMLElement {
  const div = document.createElement('div');
  div.className = 'onboarding-wrapper';
  const step = state.onboardingStep || 1;

  const totalSteps = 5;

  div.innerHTML = `
    <div class="onboarding-chrome">
      ${step < totalSteps ? `<button class="onboarding-skip" onclick="completeOnboarding()">Skip</button>` : ''}
      <div class="onboarding-progress">
        ${Array.from({ length: totalSteps }, (_, i) => i + 1).map((s) => `<div class="onboarding-dot ${s === step ? 'active' : ''} ${s < step ? 'done' : ''}"></div>`).join('')}
      </div>
    </div>
    <div class="onboarding-body" id="onboarding-body"></div>
  `;

  setTimeout(() => {
    const body = document.getElementById('onboarding-body');
    if (!body) return;
    if (step === 1) renderOnboardingWelcome(body);
    else if (step === 2) renderOnboardingLocation(body);
    else if (step === 3) renderOnboardingBudget(body);
    else if (step === 4) renderOnboardingPrefs(body);
    else renderOnboardingDone(body);
    if (window.feather) window.feather.replace();
  }, 30);

  return div;
}

export function onboardingNext(): void {
  const totalSteps = 5;
  state.onboardingStep = Math.min(totalSteps, (state.onboardingStep || 1) + 1);
  render();
}

export function onboardingBack(): void {
  state.onboardingStep = Math.max(1, (state.onboardingStep || 1) - 1);
  render();
}

// ── Step 1: Welcome ──
function renderOnboardingWelcome(container: HTMLElement): void {
  container.innerHTML = `
    <div class="ob-welcome fade-in">
      <div class="ob-logo">Swipe<span>Home</span></div>
      <h1 class="ob-title">Welcome to SwipeHome</h1>
      <p class="ob-subtitle">Deals that pencil.</p>
      <p class="ob-desc">Every property shows cap rate, cash flow, and DSCR before you tap in. Photo-Truth&trade; runs forensics on every listing photo. Swipe right to add a deal to your Watchlist.</p>

      <div class="ob-card-demo">
        <div class="ob-demo-card">
          <div class="ob-demo-img">
            <div class="ob-demo-price">$425,000</div>
          </div>
          <div class="ob-demo-info">
            <div class="ob-demo-addr">123 Maple Drive</div>
            <div class="ob-demo-meta">3 bd &middot; 2 ba &middot; 1,850 sqft</div>
          </div>
          <div class="ob-demo-arrows">
            <div class="ob-arrow-left"><i data-feather="x" width="20"></i><span>Pass</span></div>
            <div class="ob-arrow-right"><i data-feather="heart" width="20"></i><span>Like</span></div>
          </div>
        </div>
      </div>

      <button class="btn btn-primary btn-block btn-lg ob-cta" onclick="onboardingNext()">
        Let's Get Started <i data-feather="arrow-right" width="18"></i>
      </button>
    </div>
  `;
}

// ── Step 2: Location ──
function renderOnboardingLocation(container: HTMLElement): void {
  const od = state.onboardingData;
  const popularAreas = [
    { label: 'New York, NY', lat: 40.7128, lng: -74.0060 },
    { label: 'Westchester, NY', lat: 41.122, lng: -73.7949 },
    { label: 'Houston, TX', lat: 29.7604, lng: -95.3698 },
    { label: 'Los Angeles, CA', lat: 34.0522, lng: -118.2437 },
  ];

  container.innerHTML = `
    <div class="ob-step fade-in">
      <button class="ob-back" onclick="onboardingBack()"><i data-feather="arrow-left" width="18"></i></button>
      <div class="ob-icon-wrap"><i data-feather="map-pin" width="40" style="color:var(--primary)"></i></div>
      <h2 class="ob-heading">Where are you looking?</h2>
      <p class="ob-sub">We'll show you homes in your area</p>

      <button class="btn btn-primary btn-block btn-lg mb-16" id="ob-gps-btn" onclick="onboardingUseGPS()">
        <i data-feather="navigation" width="16"></i> Use My Location
      </button>

      <div class="ob-divider"><span>or</span></div>

      <div class="input-group mb-16">
        <input type="text" class="input" id="ob-city-input" placeholder="Enter a city, e.g. Austin, TX" value="${escapeHtml(od.locationText || '')}">
      </div>

      <div class="ob-popular-label">Popular areas</div>
      <div class="ob-popular-grid">
        ${popularAreas.map((a) => `
          <button class="ob-popular-chip ${od.locationText === a.label ? 'selected' : ''}"
            onclick="onboardingSelectArea('${a.label}', ${a.lat}, ${a.lng})">
            ${a.label}
          </button>
        `).join('')}
      </div>

      <button class="btn btn-primary btn-block btn-lg ob-cta" onclick="onboardingSaveLocation()">
        Continue <i data-feather="arrow-right" width="18"></i>
      </button>
    </div>
  `;
}

export function onboardingUseGPS(): void {
  const btn = document.getElementById('ob-gps-btn') as HTMLButtonElement | null;
  if (!btn) return;
  btn.disabled = true;
  btn.innerHTML = '<div class="spinner spinner-sm" style="display:inline-block;margin-right:8px"></div> Getting location...';

  if (!navigator.geolocation) {
    showToast('Geolocation not supported', 'error');
    btn.disabled = false;
    btn.innerHTML = '<i data-feather="navigation" width="16"></i> Use My Location';
    if (window.feather) window.feather.replace();
    return;
  }

  navigator.geolocation.getCurrentPosition(
    (pos) => {
      state.onboardingData.lat = pos.coords.latitude;
      state.onboardingData.lng = pos.coords.longitude;
      state.onboardingData.locationText = 'My Location';
      showToast('Location detected!', 'success');
      onboardingNext();
    },
    () => {
      showToast('Could not get location. Enter a city instead.', 'error');
      btn.disabled = false;
      btn.innerHTML = '<i data-feather="navigation" width="16"></i> Use My Location';
      if (window.feather) window.feather.replace();
    },
    { timeout: 10000 },
  );
}

export function onboardingSelectArea(label: string, lat: number, lng: number): void {
  state.onboardingData.locationText = label;
  state.onboardingData.lat = lat;
  state.onboardingData.lng = lng;
  const input = document.getElementById('ob-city-input') as HTMLInputElement | null;
  if (input) input.value = label;
  document.querySelectorAll('.ob-popular-chip').forEach((c) => c.classList.remove('selected'));
  // The inline handler uses `event.target` which is available in global onclick scope.
  // We replicate the original behavior by selecting the button that matches the label.
  const buttons = document.querySelectorAll<HTMLButtonElement>('.ob-popular-chip');
  buttons.forEach((b) => {
    if (b.textContent?.trim() === label) b.classList.add('selected');
  });
}

export function onboardingSaveLocation(): void {
  const input = document.getElementById('ob-city-input') as HTMLInputElement | null;
  const val = input ? input.value.trim() : '';
  if (val && val !== state.onboardingData.locationText) {
    state.onboardingData.locationText = val;
    state.onboardingData.lat = null;
    state.onboardingData.lng = null;
  }
  if (!state.onboardingData.locationText) {
    showToast('Please choose a location', 'error');
    return;
  }
  onboardingNext();
}

// ── Step 3: Budget ──
function renderOnboardingBudget(container: HTMLElement): void {
  const od = state.onboardingData;
  const minVal = od.minPrice || 200000;
  const maxVal = od.maxPrice || 600000;

  const quickRanges = [
    { label: 'Under $300k', min: 100000, max: 300000 },
    { label: '$300k - $500k', min: 300000, max: 500000 },
    { label: '$500k - $750k', min: 500000, max: 750000 },
    { label: '$750k - $1M', min: 750000, max: 1000000 },
    { label: '$1M+', min: 1000000, max: 5000000 },
  ];

  container.innerHTML = `
    <div class="ob-step fade-in">
      <button class="ob-back" onclick="onboardingBack()"><i data-feather="arrow-left" width="18"></i></button>
      <div class="ob-icon-wrap"><i data-feather="dollar-sign" width="40" style="color:var(--primary)"></i></div>
      <h2 class="ob-heading">What's your budget?</h2>
      <p class="ob-sub">We'll match you with homes in your range</p>

      <div class="ob-budget-display" id="ob-budget-display">
        $${minVal.toLocaleString()} &mdash; $${maxVal.toLocaleString()}
      </div>

      <div class="ob-slider-group">
        <label class="ob-slider-label">Minimum</label>
        <input type="range" class="ob-range" id="ob-min-price" min="100000" max="2000000" step="25000" value="${minVal}" oninput="onboardingUpdateBudget()">
      </div>
      <div class="ob-slider-group">
        <label class="ob-slider-label">Maximum</label>
        <input type="range" class="ob-range" id="ob-max-price" min="100000" max="5000000" step="25000" value="${maxVal}" oninput="onboardingUpdateBudget()">
      </div>

      <div class="ob-quick-label">Quick select</div>
      <div class="ob-quick-grid">
        ${quickRanges.map((r) => `
          <button class="ob-quick-chip" onclick="onboardingSetBudget(${r.min}, ${r.max})">${r.label}</button>
        `).join('')}
      </div>

      <button class="btn btn-primary btn-block btn-lg ob-cta" onclick="onboardingSaveBudget()">
        Continue <i data-feather="arrow-right" width="18"></i>
      </button>
    </div>
  `;
}

export function onboardingUpdateBudget(): void {
  const minEl = document.getElementById('ob-min-price') as HTMLInputElement | null;
  const maxEl = document.getElementById('ob-max-price') as HTMLInputElement | null;
  const display = document.getElementById('ob-budget-display');
  if (!minEl || !maxEl || !display) return;
  let minV = parseInt(minEl.value, 10);
  let maxV = parseInt(maxEl.value, 10);
  if (minV > maxV) {
    maxEl.value = String(minV);
    maxV = minV;
  }
  display.textContent = `$${minV.toLocaleString()} \u2014 $${maxV >= 5000000 ? '5M+' : maxV.toLocaleString()}`;
}

export function onboardingSetBudget(min: number, max: number): void {
  const minEl = document.getElementById('ob-min-price') as HTMLInputElement | null;
  const maxEl = document.getElementById('ob-max-price') as HTMLInputElement | null;
  if (minEl) minEl.value = String(min);
  if (maxEl) maxEl.value = String(max);
  onboardingUpdateBudget();
}

export function onboardingSaveBudget(): void {
  const minEl = document.getElementById('ob-min-price') as HTMLInputElement | null;
  const maxEl = document.getElementById('ob-max-price') as HTMLInputElement | null;
  state.onboardingData.minPrice = minEl ? parseInt(minEl.value, 10) : null;
  state.onboardingData.maxPrice = maxEl ? parseInt(maxEl.value, 10) : null;
  onboardingNext();
}

// ── Step 4: Preferences ──
function renderOnboardingPrefs(container: HTMLElement): void {
  const od = state.onboardingData;
  const bedOptions = [1, 2, 3, 4, 5];
  const bathOptions = [1, 2, 3, 4];
  const typeIcons: Record<string, string> = {
    single_family:
      '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M3 12l9-9 9 9"/><path d="M5 10v10a1 1 0 001 1h3v-6h6v6h3a1 1 0 001-1V10"/></svg>',
    condo:
      '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="4" y="2" width="16" height="20" rx="1"/><rect x="8" y="6" width="3" height="3" rx="0.5"/><rect x="13" y="6" width="3" height="3" rx="0.5"/><rect x="8" y="12" width="3" height="3" rx="0.5"/><rect x="13" y="12" width="3" height="3" rx="0.5"/><rect x="10" y="18" width="4" height="4"/></svg>',
    townhouse:
      '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M2 12l5-5 5 5"/><path d="M12 12l5-5 5 5"/><rect x="4" y="12" width="7" height="9"/><rect x="13" y="12" width="7" height="9"/><rect x="6" y="15" width="3" height="6"/><rect x="15" y="15" width="3" height="6"/></svg>',
    multi_family:
      '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="2" y="8" width="9" height="14" rx="1"/><rect x="13" y="2" width="9" height="20" rx="1"/><rect x="4" y="11" width="2" height="2"/><rect x="4" y="15" width="2" height="2"/><rect x="15" y="5" width="2" height="2"/><rect x="15" y="9" width="2" height="2"/><rect x="15" y="13" width="2" height="2"/><rect x="15" y="17" width="2" height="2"/></svg>',
  };
  const typeOptions: { value: PropertyType; label: string }[] = [
    { value: 'single_family', label: 'Single Family' },
    { value: 'condo', label: 'Condo' },
    { value: 'townhouse', label: 'Townhouse' },
    { value: 'multi_family', label: 'Multi-Family' },
  ];

  container.innerHTML = `
    <div class="ob-step fade-in">
      <button class="ob-back" onclick="onboardingBack()"><i data-feather="arrow-left" width="18"></i></button>
      <div class="ob-icon-wrap"><i data-feather="sliders" width="40" style="color:var(--primary)"></i></div>
      <h2 class="ob-heading">What are you looking for?</h2>
      <p class="ob-sub">Help us find your perfect home</p>

      <div class="ob-pref-section" data-section="beds">
        <div class="ob-pref-label">Beds</div>
        <div class="ob-pill-row">
          ${bedOptions.map((n) => `
            <button class="ob-pill ${od.beds === n ? 'selected' : ''}" data-value="${n}" onclick="onboardingSelectBeds(${n})">${n === 5 ? '5+' : n}</button>
          `).join('')}
        </div>
      </div>

      <div class="ob-pref-section" data-section="baths">
        <div class="ob-pref-label">Baths</div>
        <div class="ob-pill-row">
          ${bathOptions.map((n) => `
            <button class="ob-pill ${od.baths === n ? 'selected' : ''}" data-value="${n}" onclick="onboardingSelectBaths(${n})">${n === 4 ? '4+' : n}</button>
          `).join('')}
        </div>
      </div>

      <div class="ob-pref-section">
        <div class="ob-pref-label">Property Type</div>
        <div class="ob-type-grid">
          ${typeOptions.map((t) => `
            <button class="ob-type-card ${od.propertyTypes.includes(t.value) ? 'selected' : ''}" onclick="onboardingToggleType('${t.value}', this)">
              ${typeIcons[t.value]}
              <span>${t.label}</span>
            </button>
          `).join('')}
        </div>
      </div>

      <div class="ob-pref-section">
        <div class="ob-toggle-row">
          <div>
            <div class="ob-pref-label" style="margin-bottom:0">No HOA preferred</div>
            <div class="ob-pref-sublabel">Exclude homes with HOA fees</div>
          </div>
          <label class="toggle">
            <input type="checkbox" id="ob-no-hoa" ${!od.hoaAllowed ? 'checked' : ''}>
            <span class="toggle-slider"></span>
          </label>
        </div>
      </div>

      <button class="btn btn-primary btn-block btn-lg ob-cta" onclick="onboardingSavePrefs()">
        Continue <i data-feather="arrow-right" width="18"></i>
      </button>
    </div>
  `;
}

export function onboardingSelectBeds(n: number): void {
  state.onboardingData.beds = n;
  document.querySelectorAll<HTMLElement>('[data-section="beds"] .ob-pill').forEach((b) => {
    b.classList.toggle('selected', parseInt(b.dataset.value || '0', 10) === n);
  });
}

export function onboardingSelectBaths(n: number): void {
  state.onboardingData.baths = n;
  document.querySelectorAll<HTMLElement>('[data-section="baths"] .ob-pill').forEach((b) => {
    b.classList.toggle('selected', parseInt(b.dataset.value || '0', 10) === n);
  });
}

export function onboardingToggleType(value: PropertyType, el: HTMLElement): void {
  const types = state.onboardingData.propertyTypes;
  const idx = types.indexOf(value);
  if (idx >= 0) {
    types.splice(idx, 1);
    el.classList.remove('selected');
  } else {
    types.push(value);
    el.classList.add('selected');
  }
}

export function onboardingSavePrefs(): void {
  const hoaCheckbox = document.getElementById('ob-no-hoa') as HTMLInputElement | null;
  state.onboardingData.hoaAllowed = hoaCheckbox ? !hoaCheckbox.checked : true;
  onboardingNext();
}

// ── Final Step: All Set ──
function renderOnboardingDone(container: HTMLElement): void {
  container.innerHTML = `
    <div class="ob-done fade-in">
      <div class="ob-done-icon">
        <div class="ob-done-pulse"></div>
        <i data-feather="check-circle" width="64" style="color:var(--primary)"></i>
      </div>
      <h1 class="ob-title">You're all set!</h1>
      <p class="ob-sub">Your personalized feed is ready</p>

      <div class="ob-tips">
        <div class="ob-tip"><i data-feather="heart" width="16" style="color:var(--success)"></i> Like homes that match your vision</div>
        <div class="ob-tip"><i data-feather="x" width="16" style="color:var(--danger)"></i> Pass to refine your recommendations</div>
        <div class="ob-tip"><i data-feather="maximize-2" width="16" style="color:var(--primary)"></i> Tap a card for details</div>
      </div>

      <button class="btn btn-primary btn-block btn-lg ob-cta ob-cta-final" onclick="completeOnboarding()">
        Start Exploring <i data-feather="arrow-right" width="18"></i>
      </button>
    </div>
  `;
}

interface PrefsPayload {
  center_lat?: number;
  center_lng?: number;
  location_text?: string;
  radius_miles?: number;
  min_price?: number;
  max_price?: number;
  min_beds?: number;
  min_baths?: number;
  property_types?: PropertyType[];
  hoa_allowed?: boolean;
}

export async function completeOnboarding(): Promise<void> {
  const od = state.onboardingData;
  const payload: PrefsPayload = {};
  if (od.lat) payload.center_lat = od.lat;
  if (od.lng) payload.center_lng = od.lng;
  if (od.locationText && od.locationText !== 'My Location') payload.location_text = od.locationText;
  payload.radius_miles = 25;
  if (od.minPrice) payload.min_price = od.minPrice;
  if (od.maxPrice) payload.max_price = od.maxPrice;
  if (od.beds) payload.min_beds = od.beds;
  if (od.baths) payload.min_baths = od.baths;
  if (od.propertyTypes.length > 0) payload.property_types = od.propertyTypes;
  payload.hoa_allowed = od.hoaAllowed;

  let saved = false;
  for (let attempt = 0; attempt < 3 && !saved; attempt++) {
    try {
      await api('PATCH', '/users/preferences', payload);
      state.preferences = payload;
      saved = true;
    } catch {
      if (attempt < 2) await new Promise((r) => setTimeout(r, 500));
    }
  }
  if (!saved) {
    localStorage.setItem('swipehome_pending_prefs', JSON.stringify(payload));
    showToast('Preferences saved locally \u2014 will sync shortly', 'info');
  }

  if (od.lat && od.lng) {
    state.userLocation = { lat: od.lat, lng: od.lng };
  }

  const userId = state.user?.id || '';
  localStorage.setItem(`swipehome_onboarding_complete_${userId}`, '1');
  localStorage.setItem('swipehome_onboarding_complete', '1');
  state.onboardingStep = 1;
  state.screenStack = [];
  navigate('feed');
  loadNotificationCount();
}

export async function retryPendingPreferences(): Promise<void> {
  const pending = localStorage.getItem('swipehome_pending_prefs');
  if (!pending || !state.token) return;
  try {
    await api('PATCH', '/users/preferences', JSON.parse(pending));
    localStorage.removeItem('swipehome_pending_prefs');
  } catch {
    /* will retry next load */
  }
}

// ═══════════════════════════════════════════════════════
// Onboarding Progress & Swipe Milestones
// ═══════════════════════════════════════════════════════

export function saveOnboardingFlag(key: string, value: boolean): void {
  const userId = state.user?.id || 'anon';
  localStorage.setItem(`sh_${key}_${userId}`, value ? '1' : '0');
}

export function restoreOnboardingFlags(): void {
  const userId = state.user?.id || 'anon';
  state.hasSeenSwipeTutorial = localStorage.getItem(`sh_hasSeenSwipeTutorial_${userId}`) === '1';
  state.hasSeenDnaBuildingMsg = localStorage.getItem(`sh_hasSeenDnaBuildingMsg_${userId}`) === '1';
  state.hasSeenSwipeCelebration = localStorage.getItem(`sh_hasSeenSwipeCelebration_${userId}`) === '1';
  state.onboardingProgressDismissed = localStorage.getItem(`sh_onboardingProgressDismissed_${userId}`) === '1';
  const saved = sessionStorage.getItem(`sh_sessionSwipeCount_${userId}`);
  state.sessionSwipeCount = saved ? parseInt(saved, 10) : 0;
}

interface OnboardingStep {
  id: string;
  label: string;
  icon: string;
  completed: boolean;
  action: () => void;
}

function getOnboardingProgress(): { steps: OnboardingStep[]; completedCount: number; total: number } {
  const prefs = (state.preferences as {
    max_price?: number;
    min_price?: number;
    location_text?: string;
    min_beds?: number;
  } | null) || {};
  const hasPrefs = !!(prefs.max_price || prefs.min_price || prefs.location_text || prefs.min_beds);
  const hasSwipes = state.sessionSwipeCount > 0 || state.feedIndex > 0;
  const steps: OnboardingStep[] = [
    { id: 'preferences', label: 'Preferences', icon: '\u2699\uFE0F', completed: hasPrefs, action: () => navigate('profile') },
    { id: 'first_swipe', label: 'First Swipe', icon: '\uD83D\uDC4B', completed: hasSwipes, action: () => navigate('feed') },
  ];

  const completedCount = steps.filter((s) => s.completed).length;
  return { steps, completedCount, total: steps.length };
}

export function renderOnboardingProgressCard(): string {
  if (state.role !== 'buyer') return '';
  if (state.onboardingProgressDismissed) return '';

  const { steps, completedCount, total } = getOnboardingProgress();

  if (completedCount >= total) return '';

  const pct = Math.round((completedCount / total) * 100);
  const firstIncomplete = steps.findIndex((s) => !s.completed);

  return `
    <div class="onboarding-progress-card" id="onboarding-progress-card">
      <div class="onboarding-progress-header">
        <div class="onboarding-progress-title">Complete your profile (${completedCount}/${total})</div>
        <button class="onboarding-progress-dismiss" onclick="dismissOnboardingProgress()" title="Dismiss">&times;</button>
      </div>
      <div class="onboarding-progress-bar">
        <div class="onboarding-progress-fill" style="width:${pct}%"></div>
      </div>
      <div class="onboarding-steps">
        ${steps.map((s, i) => `
          <div class="onboarding-step ${s.completed ? 'completed' : ''} ${i === firstIncomplete ? 'active' : ''}"
               onclick="handleOnboardingStepClick(${i})">
            <div class="onboarding-step-icon">${s.completed ? '\u2713' : s.icon}</div>
            <div class="onboarding-step-label">${escapeHtml(s.label)}</div>
          </div>
        `).join('')}
      </div>
    </div>
  `;
}

export function handleOnboardingStepClick(index: number): void {
  const { steps } = getOnboardingProgress();
  const step = steps[index];
  if (step) {
    step.action();
  }
}

export function dismissOnboardingProgress(): void {
  state.onboardingProgressDismissed = true;
  saveOnboardingFlag('onboardingProgressDismissed', true);
  const card = document.getElementById('onboarding-progress-card');
  if (card) {
    card.style.transition = 'opacity 0.3s ease, max-height 0.3s ease, margin 0.3s ease, padding 0.3s ease';
    card.style.opacity = '0';
    card.style.maxHeight = '0';
    card.style.marginBottom = '0';
    card.style.padding = '0 16px';
    card.style.overflow = 'hidden';
    setTimeout(() => card.remove(), 300);
  }
}

export function showSwipeTutorial(): void {
  if (state.role !== 'buyer') return;
  if (state.hasSeenSwipeTutorial) return;

  state.hasSeenSwipeTutorial = true;
  saveOnboardingFlag('hasSeenSwipeTutorial', true);

  const overlay = document.createElement('div');
  overlay.className = 'swipe-tutorial-overlay';
  overlay.id = 'swipe-tutorial-overlay';
  overlay.onclick = () => dismissSwipeTutorial();
  overlay.innerHTML = `
    <button class="swipe-tutorial-skip" type="button" onclick="dismissSwipeTutorial()">Skip</button>
    <div class="swipe-tutorial-content">
      <div class="swipe-tutorial-gestures">
        <div class="swipe-tutorial-gesture">
          <div class="swipe-tutorial-arrow left">&larr;</div>
          <div class="swipe-tutorial-label">Pass</div>
        </div>
        <div class="swipe-tutorial-gesture">
          <div class="swipe-tutorial-arrow right">&rarr;</div>
          <div class="swipe-tutorial-label">Like</div>
        </div>
      </div>
      <div class="swipe-tutorial-hint">Every home includes Photo-Truth&trade; verification and True Cost&trade;.<br>Swipe right to save, left to pass. Tap the card for the full report.</div>
      <div class="swipe-tutorial-dismiss">Tap anywhere to continue</div>
    </div>
  `;
  document.body.appendChild(overlay);

  setTimeout(() => dismissSwipeTutorial(), 5000);
}

export function dismissSwipeTutorial(): void {
  const overlay = document.getElementById('swipe-tutorial-overlay');
  if (overlay) {
    overlay.style.opacity = '0';
    overlay.style.transition = 'opacity 0.3s ease';
    setTimeout(() => overlay.remove(), 300);
  }
}

export function checkSwipeMilestones(): void {
  if (state.role !== 'buyer') return;

  const count = state.sessionSwipeCount;

  if (count === 10 && !state.hasSeenDnaBuildingMsg) {
    state.hasSeenDnaBuildingMsg = true;
    saveOnboardingFlag('hasSeenDnaBuildingMsg', true);
    showToast('Your recommendations are learning. Every swipe makes them smarter.', 'success');
  }

  if (count === 25 && !state.hasSeenSwipeCelebration) {
    state.hasSeenSwipeCelebration = true;
    saveOnboardingFlag('hasSeenSwipeCelebration', true);
    openModal(
      'Smarter recommendations unlocked',
      `
      <div style="text-align:center;padding:8px 0">
        <div class="dna-modal-icon">\uD83C\uDFE0</div>
        <div class="dna-modal-text">
          You've reviewed 25 homes. Your preferences are dialed in and your recommendations just got sharper.<br><br>
          Check your <strong style="color:var(--primary)">For You</strong> feed to see what we found for you.
        </div>
      </div>
    `,
      [{ label: 'See My Matches', style: 'primary', onclick: () => closeModal() }],
    );
  }
}

// ═══════════════════════════════════════════════════════




interface SwipeStats {
  likes?: number;
  reviewed?: number;
}

interface LikesResponse {
  pagination?: { total_items?: number };
}

export async function loadProfileStats(): Promise<void> {
  let likes = 0;
  let reviewed = 0;
  try {
    const data = await api<SwipeStats>('GET', '/swipes/stats');
    likes = data.likes ?? 0;
    reviewed = data.reviewed ?? 0;
  } catch {
    try {
      const likesData = await api<LikesResponse>('GET', '/swipes/likes?per_page=1');
      likes = likesData?.pagination?.total_items ?? 0;
      reviewed = likes;
    } catch {
      /* use 0 defaults */
    }
  }
  const setEl = (id: string, val: number | string) => {
    const e = document.getElementById(id);
    if (e) e.textContent = String(val);
  };
  setEl('stat-likes', likes);
  setEl('stat-swipes', reviewed);
}




